// Serviço de API usando Fetch API nativo (mais seguro que axios)
import { QueryClient } from '@tanstack/react-query';

// Configuração do React Query
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutos
    },
  },
});

// Configuração da API
const API_BASE_URL = process.env.REACT_APP_PRODUCTION_API_URL
  || process.env.REACT_APP_API_URL
  || 'https://localhost:44323';

// Interface para respostas da API
export interface ApiResponse<T = any> {
  data: T;
  status: number;
  message?: string;
}

// Classe de erro personalizada
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Função para obter o token de autenticação
const getAuthToken = (): string | null => {
  return localStorage.getItem('setKeyUser');
};

// Função base para requisições HTTP
const fetchApi = async <T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> => {
  const url = `${API_BASE_URL}${endpoint}`;
  
  // Configurações padrão
  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // Adiciona token de autenticação se não for login
  const token = getAuthToken();
  if (token && !endpoint.includes('/auth/login') && !endpoint.includes('viacep.com.br')) {
    defaultHeaders.Authorization = `Bearer ${token}`;
  }

  const config: RequestInit = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    
    // Tratamento de respostas blob (como downloads de arquivos)
    if (response.headers.get('content-type')?.includes('application/json') === false) {
      if (!response.ok) {
        throw new ApiError(
          'Erro na requisição',
          response.status,
          await response.text()
        );
      }
      return {
        data: (await response.blob()) as T,
        status: response.status,
      };
    }

    const responseData = await response.json();

    if (!response.ok) {
      throw new ApiError(
        responseData.message || 'Erro na requisição',
        response.status,
        responseData
      );
    }

    return {
      data: responseData,
      status: response.status,
      message: responseData.message,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    
    // Tratamento de erros de rede
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new ApiError('Erro de conexão com o servidor', 0);
    }

    throw new ApiError(
      error instanceof Error ? error.message : 'Erro desconhecido',
      500
    );
  }
};

// Métodos HTTP
export const api = {
  get: <T = any>(endpoint: string, options?: Omit<RequestInit, 'method' | 'body'>) =>
    fetchApi<T>(endpoint, { ...options, method: 'GET' }),

  post: <T = any>(endpoint: string, data?: any, options?: Omit<RequestInit, 'method' | 'body'>) =>
    fetchApi<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),

  put: <T = any>(endpoint: string, data?: any, options?: Omit<RequestInit, 'method' | 'body'>) =>
    fetchApi<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }),

  delete: <T = any>(endpoint: string, options?: Omit<RequestInit, 'method' | 'body'>) =>
    fetchApi<T>(endpoint, { ...options, method: 'DELETE' }),

  patch: <T = any>(endpoint: string, data?: any, options?: Omit<RequestInit, 'method' | 'body'>) =>
    fetchApi<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    }),
};

// Hook para requisições GET
export const useFetch = <T = any>(
  endpoint: string,
  options?: Omit<RequestInit, 'method' | 'body'>,
  queryKey?: any[]
) => {
  return {
    queryKey: queryKey || [endpoint],
    queryFn: () => api.get<T>(endpoint, options).then(res => res.data),
  };
};

// Hook para mutações POST
export const useApiMutation = <T = any, V = any>(
  endpoint: string,
  method: 'POST' | 'PUT' | 'DELETE' | 'PATCH' = 'POST'
) => {
  return {
    mutationFn: (data?: V) => {
      switch (method) {
        case 'POST':
          return api.post<T>(endpoint, data).then(res => res.data);
        case 'PUT':
          return api.put<T>(endpoint, data).then(res => res.data);
        case 'DELETE':
          return api.delete<T>(endpoint).then(res => res.data);
        case 'PATCH':
          return api.patch<T>(endpoint, data).then(res => res.data);
        default:
          throw new Error(`Método não suportado: ${method}`);
      }
    },
  };
};

export default api;
